import { z } from 'zod';
import { type CriterionKey, SeveritySchema, type Severity } from './evaluation.js';

// In-app alert engine (spec §24, decision 3): rule definitions derived from criterion results, a real state
// machine, dedupe per subject, auto-resolution when the condition clears, audit trail, and a delivery-channel
// abstraction with only the in-app channel implemented.

export const ALERT_STATES = ['open', 'acknowledged', 'snoozed', 'resolved', 'auto_resolved', 'expired'] as const;
export const AlertStateSchema = z.enum(ALERT_STATES);
export type AlertState = z.infer<typeof AlertStateSchema>;

export const ACTIVE_ALERT_STATES: readonly AlertState[] = ['open', 'acknowledged', 'snoozed'];
export const TERMINAL_ALERT_STATES: readonly AlertState[] = ['resolved', 'auto_resolved', 'expired'];

/** Allowed transitions. Terminal states never transition again (a recurrence opens a new alert). */
export const ALERT_TRANSITIONS: Record<AlertState, readonly AlertState[]> = {
  open: ['acknowledged', 'snoozed', 'resolved', 'auto_resolved', 'expired'],
  acknowledged: ['snoozed', 'resolved', 'auto_resolved', 'expired'],
  snoozed: ['open', 'acknowledged', 'resolved', 'auto_resolved', 'expired'],
  resolved: [],
  auto_resolved: [],
  expired: [],
};

export function canTransition(from: AlertState, to: AlertState): boolean {
  return ALERT_TRANSITIONS[from].includes(to);
}

export const ALERT_CHANNELS = ['in_app'] as const;
export type AlertChannelKey = (typeof ALERT_CHANNELS)[number];

export const ALERT_RULE_KEYS = [
  'new_lead_unattended',
  'prospect_waiting',
  'hot_lead_without_appointment',
  'question_ignored',
  'follow_up_overdue',
  'conversation_abandoned',
  'insufficient_follow_up',
] as const;
export const AlertRuleKeySchema = z.enum(ALERT_RULE_KEYS);
export type AlertRuleKey = z.infer<typeof AlertRuleKeySchema>;

export interface AlertRuleDef {
  key: AlertRuleKey;
  label: string;
  /** Spanish icon cue used with the label (never color-only). */
  emoji: string;
  description: string;
  source: 'deterministic' | 'ai';
  /** Criterion whose current result raises the alert. */
  criterionKey: CriterionKey;
  /** Default priority (some rules escalate from the criterion severity). */
  priority: Severity;
  /** Active alerts expire after this many hours even if the condition persists. */
  expiresAfterHours: number;
}

export const ALERT_RULES: readonly AlertRuleDef[] = [
  { key: 'new_lead_unattended', label: 'Lead nuevo sin atender', emoji: '🔴', description: 'Un lead escribió y aún no tiene primer contacto fuera del SLA.', source: 'deterministic', criterionKey: 'first_contact_sla', priority: 'high', expiresAfterHours: 72 },
  { key: 'prospect_waiting', label: 'Prospecto esperando respuesta', emoji: '🔴', description: 'El prospecto escribió y lleva más del SLA sin respuesta.', source: 'deterministic', criterionKey: 'lead_waiting', priority: 'high', expiresAfterHours: 72 },
  { key: 'hot_lead_without_appointment', label: 'Lead caliente sin cita', emoji: '🔥', description: 'Varias señales de alta intención y no se ha propuesto una visita.', source: 'ai', criterionKey: 'hot_lead_without_appointment', priority: 'critical', expiresAfterHours: 168 },
  { key: 'question_ignored', label: 'Pregunta ignorada', emoji: '⚠️', description: 'Una pregunta del prospecto quedó sin responder o fue evadida.', source: 'ai', criterionKey: 'questions_answered', priority: 'medium', expiresAfterHours: 168 },
  { key: 'follow_up_overdue', label: 'Follow-up vencido', emoji: '⚠️', description: 'Venció el siguiente seguimiento de la cadencia.', source: 'deterministic', criterionKey: 'follow_up_overdue', priority: 'medium', expiresAfterHours: 168 },
  { key: 'conversation_abandoned', label: 'Conversación abandonada', emoji: '⚠️', description: 'El último mensaje del prospecto lleva demasiado tiempo sin respuesta.', source: 'deterministic', criterionKey: 'lead_waiting', priority: 'medium', expiresAfterHours: 168 },
  { key: 'insufficient_follow_up', label: 'Seguimiento insuficiente', emoji: '⚠️', description: 'El prospecto fue abandonado después de un único intento.', source: 'deterministic', criterionKey: 'single_touch_abandonment', priority: 'low', expiresAfterHours: 168 },
];
export const ALERT_RULES_BY_KEY = Object.fromEntries(ALERT_RULES.map((r) => [r.key, r])) as Record<AlertRuleKey, AlertRuleDef>;

export const ALERT_STATE_LABELS: Record<AlertState, string> = {
  open: 'Nueva',
  acknowledged: 'Vista',
  snoozed: 'Pospuesta',
  resolved: 'Resuelta',
  auto_resolved: 'Resuelta automáticamente',
  expired: 'Expirada',
};

export const ALERT_PRIORITY_LABELS: Record<Severity, string> = { low: 'Baja', medium: 'Media', high: 'Alta', critical: 'Crítica' };

// ───────── Wire format ─────────

export const AlertSchema = z.object({
  id: z.string(),
  ruleKey: AlertRuleKeySchema,
  state: AlertStateSchema,
  priority: SeveritySchema,
  title: z.string(),
  detail: z.string().nullable(),
  conversationId: z.string(),
  episodeId: z.string(),
  contactName: z.string(),
  connectionId: z.string(),
  connectionLabel: z.string(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  evidenceMessageIds: z.array(z.string()),
  openedAt: z.string(),
  lastSeenAt: z.string(),
  acknowledgedAt: z.string().nullable(),
  snoozedUntil: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolution: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type Alert = z.infer<typeof AlertSchema>;

export const AlertListQuerySchema = z.object({
  /** active = open + acknowledged + snoozed. */
  view: z.enum(['active', 'open', 'snoozed', 'closed', 'all']).default('active'),
  priority: SeveritySchema.optional(),
  ruleKey: AlertRuleKeySchema.optional(),
  agentId: z.string().max(64).optional(),
  connectionId: z.string().max(64).optional(),
  conversationId: z.string().max(64).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AlertListQuery = z.infer<typeof AlertListQuerySchema>;

export const AlertPageSchema = z.object({ items: z.array(AlertSchema), total: z.number().int(), nextCursor: z.string().nullable() });
export type AlertPage = z.infer<typeof AlertPageSchema>;

export const AlertSummarySchema = z.object({
  /** Open + acknowledged (snoozed excluded) by priority. */
  active: z.object({ low: z.number().int(), medium: z.number().int(), high: z.number().int(), critical: z.number().int() }),
  open: z.number().int(),
  snoozed: z.number().int(),
});
export type AlertSummary = z.infer<typeof AlertSummarySchema>;

export const AlertActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('acknowledge') }),
  z.object({ action: z.literal('snooze'), minutes: z.number().int().min(5).max(7 * 24 * 60) }),
  z.object({ action: z.literal('resolve'), note: z.string().trim().max(500).nullable().optional() }),
]);
export type AlertAction = z.infer<typeof AlertActionSchema>;

export const AlertEventSchema = z.object({
  id: z.string(),
  fromState: AlertStateSchema.nullable(),
  toState: AlertStateSchema,
  actorName: z.string().nullable(),
  reason: z.string(),
  at: z.string(),
});
export const AlertDetailSchema = AlertSchema.extend({ events: z.array(AlertEventSchema) });
export type AlertDetail = z.infer<typeof AlertDetailSchema>;
